import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import { EuroFormatter } from "../utils/EuroFormatter";
import { Loading } from "../ui/Loading";
import { oraPrenotaStore } from "../oraPrenotaStore";
import { selectRole } from "../utils/selectors";

export interface PagamentoSatispayRow {
  id: number;
  t24_user_id: number;
  ClubId: number;
  satispay_id: string;
  code_identifier: string;
  insert_date: string;
  expire_date: string;
  amount_unit: number;
  status: string;
  clubNome: string | null;
  nome: string | null;
  cognome: string | null;
  email: string | null;
}

const STATI = ["PENDING", "ACCEPTED", "EXPIRED", "CANCELED"] as const;

function etichettaStato(status: string) {
  switch (status) {
    case "PENDING":
      return "In attesa";
    case "ACCEPTED":
      return "Accettato";
    case "EXPIRED":
      return "Scaduto";
    case "CANCELED":
      return "Annullato";
    case "REFUNDED":
      return "Rimborsato";
    case "FAILED":
      return "Fallito";
    default:
      return status;
  }
}

function coloreStato(status: string): "warning" | "success" | "error" | "default" {
  switch (status) {
    case "PENDING":
      return "warning";
    case "ACCEPTED":
      return "success";
    case "CANCELED":
    case "FAILED":
      return "error";
    default:
      return "default";
  }
}

function formattaData(value: string) {
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return "";
  return format(data, "dd/MM/yyyy HH:mm");
}

function nominativo(row: PagamentoSatispayRow) {
  const nome = [row.cognome, row.nome].filter(Boolean).join(" ");
  if (nome.length > 0) return nome;
  if (row.email) return row.email;
  return "Utente " + row.t24_user_id;
}

function messaggioVerifica(payload: any) {
  if (payload && payload.esito === "Ricarica effettuata") {
    return "Pagamento accettato: il conto è stato accreditato.";
  }
  const status = payload?.status ?? payload?.satispayResponseData?.status;
  if (status === "PENDING") return "Satispay segnala il pagamento ancora in attesa.";
  if (status === "EXPIRED") return "Satispay segnala il pagamento scaduto.";
  if (status === "CANCELED") return "Satispay segnala il pagamento annullato.";
  if (status === "ACCEPTED") return "Pagamento accettato.";
  if (typeof payload?.esito === "string" && payload.esito.length > 0) return payload.esito;
  return "Stato aggiornato.";
}

export function PagamentiSatispay() {
  const role = oraPrenotaStore(selectRole);
  const isAdmin = role === "ADMIN";
  const { enqueueSnackbar } = useSnackbar();
  const [stato, setStato] = useState("TUTTI");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [verificaId, setVerificaId] = useState<number | null>(null);

  const { isLoading, isError, data, refetch, isFetching } = useQuery<
    { payments: PagamentoSatispayRow[] },
    AxiosError
  >(
    ["ricarica/pagamentiSatispay"],
    () => axios.get(API_URL + "ricarica/pagamentiSatispay").then((res) => res.data),
    { refetchOnWindowFocus: false },
  );

  const rows = (data?.payments ?? []).filter((row) => stato === "TUTTI" || row.status === stato);
  const pagina = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const verifica = (id: number) => {
    setVerificaId(id);
    axios
      .post(API_URL + "ricarica/pagamentiSatispay/" + id + "/verifica")
      .then((res) => {
        enqueueSnackbar(messaggioVerifica(res.data?.verifySatispayPayment), { variant: "info" });
        refetch();
      })
      .catch((error: AxiosError<{ message?: string | string[] }>) => {
        const message = error.response?.data?.message;
        const testo = Array.isArray(message) ? message.join(", ") : message;
        enqueueSnackbar(testo || "Verifica non riuscita", { variant: "error" });
      })
      .finally(() => setVerificaId(null));
  };

  if (isLoading) return <Loading />;
  if (isError) {
    return (
      <Typography>
        Non è stato possibile caricare i pagamenti Satispay. Verifica di essere entrato come amministratore.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h5">Pagamenti Satispay</Typography>
      <Typography variant="body2">
        {isAdmin
          ? "Elenco di tutti i pagamenti Satispay registrati. Per un pagamento in attesa puoi rileggere lo stato su Satispay: se risulta accettato, il conto del socio viene accreditato."
          : "Elenco dei pagamenti Satispay del tuo club. Per un pagamento in attesa puoi rileggere lo stato su Satispay: se risulta accettato, il conto del socio viene accreditato."}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          select
          size="small"
          label="Stato"
          value={stato}
          onChange={(event) => {
            setStato(event.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="TUTTI">Tutti</MenuItem>
          {STATI.map((value) => (
            <MenuItem key={value} value={value}>
              {etichettaStato(value)}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={() => refetch()} disabled={isFetching}>
          Aggiorna elenco
        </Button>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              {isAdmin && <TableCell>Club</TableCell>}
              <TableCell>Socio</TableCell>
              <TableCell align="right">Importo</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Id Satispay</TableCell>
              <TableCell>Scadenza</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagina.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{formattaData(row.insert_date)}</TableCell>
                {isAdmin && <TableCell>{row.clubNome || row.ClubId}</TableCell>}
                <TableCell>{nominativo(row)}</TableCell>
                <TableCell align="right">{EuroFormatter.format(row.amount_unit / 100)}</TableCell>
                <TableCell>
                  <Chip size="small" label={etichettaStato(row.status)} color={coloreStato(row.status)} />
                </TableCell>
                <TableCell sx={{ maxWidth: 220, wordBreak: "break-all" }}>{row.satispay_id}</TableCell>
                <TableCell>{formattaData(row.expire_date)}</TableCell>
                <TableCell>
                  {row.status === "PENDING" && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={verificaId !== null}
                      onClick={() => verifica(row.id)}
                    >
                      {verificaId === row.id ? "Verifica..." : "Verifica stato"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {pagina.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7}>Nessun pagamento Satispay trovato.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Righe"
        />
      </Box>
    </Stack>
  );
}
